import { CSSProperties, useCallback, useMemo } from 'react'
import { LoggedOutGeneratorSection } from '../../LoggedOutCodeGenerator.types'
import { SystemStyleObject } from '@mui/system'
import { TRANSITION_DURATION } from '../../LoggedOutCodeGenerator.constants'
import { Theme } from '@mui/material'
import {
  selectFirstSectionIsActive,
  selectSecondSectionIsActive,
  selectSectionGroup
} from '@dtx-company/app/shared/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@dtx-company/app/shared/common/src/hooks/useGlobalServices'
import { usePreviousValue } from '@dtx-company/true-common/src/hooks/usePreviousValue/usePreviousValue'
import { useSelector } from '@xstate/react'

const commonTransitionStyles: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0
}
const transitionPropertyStyle: CSSProperties = {
  transition: `opacity ${TRANSITION_DURATION}ms, transform ${TRANSITION_DURATION}ms ease-in-out`
}
const transitionEnterActiveAndExitStyles: CSSProperties = {
  opacity: 1,
  transform: 'none'
}

interface BuildTransitionStyleArgs {
  sectionName: LoggedOutGeneratorSection
  containerHeight: string
}

// when scrolling up
const buildEnterFromTopStyles = ({
  sectionName,
  containerHeight
}: BuildTransitionStyleArgs): SystemStyleObject<Theme> => ({
  [`.${sectionName}-enter`]: {
    ...commonTransitionStyles,
    transform: `translateY(-${containerHeight})`,
    opacity: 0
  },
  [`.${sectionName}-enter-active`]: {
    ...commonTransitionStyles,
    ...transitionEnterActiveAndExitStyles,
    ...transitionPropertyStyle
  },
  [`.${sectionName}-exit`]: {
    ...commonTransitionStyles,
    ...transitionEnterActiveAndExitStyles
  },
  [`.${sectionName}-exit-active`]: {
    ...commonTransitionStyles,
    ...transitionPropertyStyle,
    opacity: 0,
    transform: `translateY(-${containerHeight})`
  }
})

// when scrolling down
const buildEnterFromBottomStyles = ({
  sectionName,
  containerHeight
}: BuildTransitionStyleArgs): SystemStyleObject<Theme> => ({
  [`.${sectionName}-enter`]: {
    ...commonTransitionStyles,
    opacity: 0,
    transform: `translateY(${containerHeight})`
  },
  [`.${sectionName}-enter-active`]: {
    ...commonTransitionStyles,
    ...transitionEnterActiveAndExitStyles,
    ...transitionPropertyStyle
  },
  [`.${sectionName}-exit`]: {
    ...commonTransitionStyles,
    ...transitionEnterActiveAndExitStyles
  },
  [`.${sectionName}-exit-active`]: {
    ...commonTransitionStyles,
    ...transitionPropertyStyle,
    opacity: 0,
    transform: `translateY(${containerHeight})`
  }
})

interface LOCGSectionTransitionStyles {
  scanDestinationSectionStyles: SystemStyleObject<Theme>
  codeDesignSectionStyles: SystemStyleObject<Theme>
  authSectionStyles: SystemStyleObject<Theme>
}

export const useLOCGSectionTransitionStyles = (
  containerHeight: string
): LOCGSectionTransitionStyles => {
  const { locgService } = useGlobalServices()
  const currentSection = useSelector(locgService, selectSectionGroup)
  const previousSection = usePreviousValue(currentSection)
  const firstSectionIsActive = useSelector(locgService, selectFirstSectionIsActive)
  const secondSectionIsActive = useSelector(locgService, selectSecondSectionIsActive)

  const buildFirstSectionStyles = useCallback(
    (sectionName: LoggedOutGeneratorSection) => {
      return buildEnterFromTopStyles({
        sectionName,
        containerHeight
      })
    },
    [containerHeight]
  )

  const buildMiddleSectionStyles = useCallback(
    (sectionName: LoggedOutGeneratorSection) => {
      const args = {
        sectionName,
        containerHeight
      }
      if (firstSectionIsActive) {
        return buildEnterFromBottomStyles(args)
      }
      if (currentSection === LoggedOutGeneratorSection.SignUp) {
        return buildEnterFromTopStyles(args)
      }
      if (secondSectionIsActive) {
        if (
          previousSection === LoggedOutGeneratorSection.ScanDestination ||
          previousSection === LoggedOutGeneratorSection.CodeDesign
        ) {
          return buildEnterFromBottomStyles(args)
        }
        if (previousSection === LoggedOutGeneratorSection.SignUp) {
          return buildEnterFromTopStyles(args)
        }
      }
      return buildEnterFromBottomStyles(args)
    },
    [containerHeight, currentSection, firstSectionIsActive, previousSection, secondSectionIsActive]
  )

  const buildLastSectionStyles = useCallback(
    (sectionName: LoggedOutGeneratorSection) => {
      return buildEnterFromBottomStyles({
        sectionName,
        containerHeight
      })
    },
    [containerHeight]
  )

  const scanDestinationSectionStyles = useMemo(() => {
    return buildMiddleSectionStyles(LoggedOutGeneratorSection.ScanDestination)
  }, [buildMiddleSectionStyles])

  const codeDesignSectionStyles = useMemo(() => {
    return buildFirstSectionStyles(LoggedOutGeneratorSection.CodeDesign)
  }, [buildFirstSectionStyles])

  const authSectionStyles = useMemo(() => {
    return buildLastSectionStyles(LoggedOutGeneratorSection.SignUp)
  }, [buildLastSectionStyles])

  return {
    scanDestinationSectionStyles,
    codeDesignSectionStyles,
    authSectionStyles
  }
}
